import { TradeInAemNavStep, TradeInAemSearchType } from "../../../utils/constants";
import { TradeInCsl } from "../../TradeInCsl";
import {
    TradeInAemFindVehicleStatus,
    TradeInAemResolveItemType,
    type TradeInAemEHubPayload,
    type TradeInAemExtraVehicleDetailsPdfBaseType,
    type TradeInAemFetchVehicleResultsReturnType,
    type TradeInAemResolveItemConfig,
    type TradeInAemVehicleExtraInfoType,
    type TradeInAemVehicleNameType,
    type TradeInAemVehicleType,
    type TradeInAemVehicleValueType,
} from "../../../utils/types";
import { fetchFindVehicle, getParsedVehicleDetails, handleVehicleFetch } from "../../../utils/utils";
import type { TradeInAemStateType } from "../../../redux/store";
import { type TradeInAemLabelType, getTradeInAemLabel } from "../../../utils/labels";
import { formatNumber } from "../../../../common-deprecated/Globalize";
import {
    tradeInAemAddResolveItem,
    tradeInAemFindVehicleSetSearchResults,
    tradeInAemResetResolveItems,
    tradeInAemSetResolveVehicle,
} from "../../../redux/actions/TradeInAemFindVehicleActions";
import type { DealerFinderSettingsType } from "../../../../common-deprecated/components/AemDealerFinder";
import { tradeInAemVehicleSetVehicle } from "../../../redux/actions/TradeInAemVehicleActions";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
export type TradeInAemVehicleTypeBg = {
    constructionMonth: string;
    constructionYear: string;
    datConstructionTime: string;
    dateContainer: string;
    datEcode: string;
    doorCount: string;
    enginePowerHp: string;
    enginePowerKw: string;
    engineVol: string;
    fuelType: string;
    gearboxType: string;
    plateNum: string;
    plate?: string; // Resolve flow uses plate instead of plateNum.
    seatCount: string;
    vehicleBrand: string;
    vehicleModelDat: string;
    vehicleType: string;
    vin: string;

    // Extra info property - used to save some data which will be used for the PDF later
    tmeExtraInfo?: TradeInAemVehicleExtraInfoType;
};

// TBG has no getValue call
export type TradeInAemBgValueApiResponseType = object;

// Specific properties which are only shown for DAT in the vehicle extra details in the PDF
export type TradeInAemExtraVehicleDetailsPdfBgSpecificType = {
    plateNum: string;
};

// Combination of base props + TBG specific props.
// Note that the PDF is enabled as there is no value call for TBG.
type TradeInAemBgExtraVehicleDetailsPdfType = TradeInAemExtraVehicleDetailsPdfBaseType &
    TradeInAemExtraVehicleDetailsPdfBgSpecificType;

type SearchVehicleByVinResultType = {
    requestId: string;
    vehicleInfo?: TradeInAemVehicleTypeBg;
    car?: { vin: string };
    plate?: { input: string };
    brand?: object;
};

// ----------------------------------------------------------------------
// CSL
// ----------------------------------------------------------------------

export class TradeInCslBg extends TradeInCsl {
    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    enabledNavSteps = [
        TradeInAemNavStep.DealerFinder,
        TradeInAemNavStep.FindVehicle,
        TradeInAemNavStep.VehicleDetails,
        TradeInAemNavStep.ContactForm,
        TradeInAemNavStep.ModelSelect,
        TradeInAemNavStep.Summary,
    ];

    enableVin = true;

    enableLicensePlate = false;

    // Resolve is disabled for TBG by default.
    // However, if the VIN lookup is insufficient, the resolve flow will be shown nevertheless.
    enableResolve = false;

    enablePreviouslySearchedVehicles = false;

    resolveKeyMap: Record<string, TradeInAemLabelType> = {
        plate: "tradeInAemResolvePlate",
        brand: "tradeInAemResolveManufacturer",
        model: "tradeInAemResolveModel",
        fuel: "tradeInAemResolveFuel",
        doors: "tradeInAemResolveDoors",
        power: "tradeInAemResolvePower",
        year: "tradeInAemResolveYear",
        transmission: "tradeInAemResolveTransmission",
        wheeldrive: "tradeInAemResolveWheeldrive",
    };

    dealerFinderSettings: DealerFinderSettingsType = {};

    hideValuationValueOnSummary = true;

    testVins = [
        {
            value: "iO-test-01",
            description:
                "Test VIN which is saved in the DAT database. If removed from the database, resolve until a new car is saved.",
        },
    ];

    // TBG has no getValue call. Instead the dealer will create a valuation manually by checking the vehicle data.
    vehicleValueSelector(): TradeInAemVehicleValueType | null {
        return null;
    }

    vehiclePlateOrVinSelector(state: TradeInAemStateType): string {
        const { searchType } = state.tradeInAemFindVehicle;
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeBg;
        return searchType === TradeInAemSearchType.LicensePlate ? vehicle.plateNum : vehicle.vin;
    }

    vehicleInfoBlockSelector(state: TradeInAemStateType): { label: string; value: string }[] {
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeBg;
        if (!vehicle) return [];

        const { formItems } = state.tradeInAemVehicleDetails;
        // The extra form items will be parsed and shown
        const { mileage, ...extraFormItems } = formItems;
        const mileageValue = formatNumber(Number(mileage.value as string), state.commonSettings.culture.name);
        const mileageLabel = getTradeInAemLabel(state, "tradeInAemMileageFormat").replace("{value}", mileageValue);

        return [
            {
                label: getTradeInAemLabel(state, "tradeInAemVehicleDetailsVin"),
                value: vehicle.vin,
            },
            { label: getTradeInAemLabel(state, "tradeInAemVehicleDetailsLicensePlate"), value: vehicle.plateNum },
            { label: mileage.label, value: mileageLabel },
            ...getParsedVehicleDetails(extraFormItems),
        ];
    }

    // TBG has no getValue call and thus no quote. Instead the dealer will create a valuation manually by checking the vehicle data.
    vehicleQuoteExpirySelector(): string {
        return "";
    }

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------
    getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType {
        const typedVehicle = vehicle as TradeInAemVehicleTypeBg;
        return {
            name: `${typedVehicle.vehicleBrand} ${typedVehicle.vehicleModelDat}`,
            description: typedVehicle.vehicleType,
        };
    }

    getVehicleKey(vehicle: TradeInAemVehicleType): string {
        const typedVehicle = vehicle as TradeInAemVehicleTypeBg;
        return `${typedVehicle.vehicleBrand}-${typedVehicle.vehicleModelDat}-${typedVehicle.vin}`;
    }

    // Spread out the data of vehicleInfo. We want to store the key/values directly instead of having it in a sub-object.
    // Note that we also map the plateNum so we also have it as a plate. Plate is used in resolve, PlateNum is used in search. This is important so the search and resolve flow work in parallel.
    parseVehicleInfo(vehicleInfo: TradeInAemVehicleTypeBg): TradeInAemVehicleTypeBg {
        return { ...vehicleInfo, plate: vehicleInfo.plateNum };
    }

    async fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void> {
        const { dispatch } = this.getStore();
        const { commonSettings, tradeInAemSettings } = this.getStore().getState();
        let status: TradeInAemFindVehicleStatus | undefined;

        const fetchFn = async (): Promise<TradeInAemFetchVehicleResultsReturnType> => {
            dispatch(tradeInAemResetResolveItems([])); // Always reset form if VIN search is called.
            dispatch(tradeInAemVehicleSetVehicle({})); // Also reset the previously searched vehicle result.

            const headers = this.getTradeInApiHeaders("GET");
            const response = await fetchFindVehicle(value, type, commonSettings, tradeInAemSettings, headers);
            if (response.status === 200) {
                const jsonResponse: SearchVehicleByVinResultType = await response.json();

                // If we don't receive a full vehicle object (.vehicleInfo) more data is required. Thus we enable the insufficientData flow.
                if (!jsonResponse.vehicleInfo && !!jsonResponse.plate) {
                    dispatch(tradeInAemSetResolveVehicle({ ...jsonResponse, vin: jsonResponse.car?.vin }));
                    status = TradeInAemFindVehicleStatus.Insufficient;
                }

                return {
                    results: jsonResponse.vehicleInfo ? [this.parseVehicleInfo(jsonResponse.vehicleInfo)] : [],
                    status,
                };
            } else {
                return { results: [] };
            }
        };

        await handleVehicleFetch(dispatch, fetchFn);
    }

    // TBG has no getValue call and thus no quote
    async fetchVehicleQuote(): Promise<void> {
        return Promise.resolve();
    }

    getEHubVehicleData(): TradeInAemEHubPayload["vehicle"] {
        const currentState = this.getStore().getState();
        const vehicleName = this.vehicleNameSelector(currentState);
        const { tradeInAemVehicle, tradeInAemVehicleDetails } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeBg;

        return {
            quoteId: null,
            licensePlate: vehicle.plateNum,
            name: `${vehicleName.name} - ${vehicleName.description || ""}`,
            manufacturer: vehicle.vehicleBrand,
            model: vehicle.vehicleModelDat,
            bodyType: vehicle.vehicleType,
            mileage: (tradeInAemVehicleDetails.formItems?.mileage?.value as string) || null,
            doors: vehicle.doorCount,
            fuelType: vehicle.fuelType,
            engine: null,
            transmission: vehicle.gearboxType,
            modelYear: vehicle.constructionYear,
            co2: null,
            vin: vehicle.vin,
            pdfLink: null,
            registrationDate: `${vehicle.constructionYear}-${vehicle.constructionMonth}`,
        };
    }

    // The PDF is not shown as there is no value call for TBG.
    getExtraVehicleDetailsPdf = (): TradeInAemBgExtraVehicleDetailsPdfType => {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeBg;

        const baseObject: TradeInAemExtraVehicleDetailsPdfBaseType = {
            manufacturer: vehicle.vehicleBrand,
            model: vehicle.vehicleModelDat,
            grade: null,
            generation: null,
            engine: null,
            powerHp: vehicle.enginePowerHp,
            powerPs: null,
            powerKw: vehicle.enginePowerKw,
            body: vehicle.vehicleType,
            doors: vehicle.doorCount,
            fuel: vehicle.fuelType,
            transmission: vehicle.gearboxType,
            gears: null,
            colour: null,
            regDate: `${vehicle.constructionYear}-${vehicle.constructionMonth}`,
            vin: vehicle.vin,
            seats: vehicle.seatCount,
            wheeldrive: null,
            cc: vehicle.engineVol,
            year: vehicle.constructionYear,
        };

        // TBG specific
        const csObject: TradeInAemExtraVehicleDetailsPdfBgSpecificType = { plateNum: vehicle.plateNum };

        return { ...baseObject, ...csObject };
    };

    getResolveOptions = (parsedResponse: Record<string, any>): boolean => {
        const { dispatch } = this.getStore();
        let newItem = false;

        Object.keys(parsedResponse).forEach((key) => {
            const isSelect = typeof parsedResponse[key]?.select !== "undefined";
            const isInput = typeof parsedResponse[key]?.input !== "undefined";
            if (isSelect || isInput) {
                newItem = true;

                // A resolveItem can be an input or select field, map accordingly:
                const resolveItem: TradeInAemResolveItemConfig = {
                    type: isInput ? TradeInAemResolveItemType.Input : TradeInAemResolveItemType.Select,
                    vehicleProperty: key,
                    labelKey: this.resolveKeyMap[key] || key,
                    options: isSelect
                        ? parsedResponse[key].select.map((selectItem: { name: string; code: string }) => ({
                              label: selectItem.name,
                              id: selectItem.code,
                          }))
                        : [],
                    required: true,
                    selectedOptionId: isSelect ? "" : null,
                };
                dispatch(tradeInAemAddResolveItem(resolveItem));
            }
        });
        return newItem;
    };

    formatResolveVehicleResponse(parsedResponse: Record<string, any>): Record<string, any> {
        const { dispatch } = this.getStore();
        const status = this.getStore().getState().tradeInAemFindVehicle.searchStatus;

        // The vehicleInfo object means that a vehicle has been identified correctly. Return all children instead of the parent.
        if (parsedResponse.vehicleInfo) {
            const result = this.parseVehicleInfo(parsedResponse.vehicleInfo);
            dispatch(tradeInAemFindVehicleSetSearchResults([result], status)); // Dispatch the matched vehicle to the store. This will make the resolve options disabled as this result is permanently matched to the provided VIN.
            return result; // Return an object of all children of parsedResponse.vehicleInfo.
        }

        // If no match is found yet (resolve is still ongoing), it will return some data keys directly in the response, and also some data keys in a sub-object called 'car'.
        // We want all data to be in the root object. Thus we combine the 2 into 1 object accordingly. Also remove the car sub-object as this is no longer needed.
        const restructuredResponse = {
            ...parsedResponse,
            ...parsedResponse.car,
        };

        delete restructuredResponse.car;

        return restructuredResponse;
    }

    getStaticResolveOptions = (): string[] => ["plate", "vin", "plateNum", "manufacturer"];

    prefillFormsWithTestData(): void {
        super.prefillFormsWithTestData();

        // Prefill custom vehicle details values
        this.setFormItemValue("mileage", "100000");
    }
}
